import { useCallback, useEffect, useMemo, useState } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import {
  safeStringify,
  wrapWithEventPropertiesPrefix,
} from '@adsk/exo-skeleton-api/internal';

import { ANALYTICS_TYPES } from '../analytics';
import { EXO_SKELETON_SCOPE_NAME } from '../consts';
import { formatAnnouncementDateAndTime } from '../utils/formatAnnouncementDateAndTime';
import { safeParseObjectFromLocalStorage } from '../utils/safeParseObjectFromLocalStorage';
import { testUserAgentRegex } from '../utils/testUserAgentRegex';

export const ANNOUNCEMENT_BANNER_REPORT_TYPES = {
  BANNER_VISIBLE: 'announcement-banner-visible',
  TEXT_EMPTY: 'announcement-banner-text-empty',
  USER_AGENT_REGEX_NOT_MATCHED:
    'announcement-banner-user-agent-regex-not-matched',
  END_DATE_BEFORE_START_DATE: 'announcement-banner-end-date-before-start-date',
  FAILED_TO_FORMAT_DATE: 'announcement-banner-failed-to-format-date',
  UNKNOWN_FIELDS_IN_FEATURE_FLAG:
    'announcement-banner-unknown-fields-in-feature-flag',
  USER_DISMISSED: 'announcement-banner-user-dismissed',
};

// join all values of the announcement object to create a unique identifier
function getAnnouncementUUID(announcement: Announcement) {
  return Object.values(announcement)
    .filter((v) => v)
    .join('_');
}

// load the dismissed state from the local storage
function loadDismissedState(announcementId: string) {
  return safeParseObjectFromLocalStorage(announcementId) || false;
}

// save the dismissed state to the local storage
function saveDismissedState({
  announcementId,
  isDismissed,
}: {
  announcementId: string;
  isDismissed: boolean;
}) {
  localStorage.setItem(announcementId, JSON.stringify(isDismissed));
}

export type Announcement = {
  text?: string;
  startDate?: string;
  endDate?: string;
  userAgentRegex?: string;
  allowDismiss?: boolean;
};

export const useAnnouncement = (announcement: Announcement) => {
  const { text, startDate, endDate, allowDismiss, userAgentRegex } =
    announcement;

  const announcementId = useMemo(
    () => getAnnouncementUUID(announcement),
    [announcement],
  );

  const [isDismissed, setIsDismissed] = useState(
    loadDismissedState(announcementId),
  );

  useEffect(() => {
    // update the dismissed state when the announcement changes
    setIsDismissed(loadDismissedState(announcementId));
  }, [announcementId]);

  useEffect(() => {
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      const context = {
        announcement,
        startDate,
        endDate,
      };

      report({
        scope: EXO_SKELETON_SCOPE_NAME,
        type: REPORT_TYPES.warning,
        value: ANNOUNCEMENT_BANNER_REPORT_TYPES.END_DATE_BEFORE_START_DATE,
        context: {
          ...context,
          ...wrapWithEventPropertiesPrefix({
            data: safeStringify(context),
            message:
              ANNOUNCEMENT_BANNER_REPORT_TYPES.END_DATE_BEFORE_START_DATE,
            type: REPORT_TYPES.warning,
          }),
        },
      });
    }
  }, [endDate, startDate, announcement]);

  const safelyFormatDateAndReportOnFailure = useCallback(
    (date) => {
      if (date) {
        try {
          return formatAnnouncementDateAndTime(date);
        } catch (error) {
          report({
            scope: EXO_SKELETON_SCOPE_NAME,
            type: REPORT_TYPES.error,
            value: ANNOUNCEMENT_BANNER_REPORT_TYPES.FAILED_TO_FORMAT_DATE,
            context: {
              announcement,
              date,
              error,
              ...wrapWithEventPropertiesPrefix({
                data: safeStringify({
                  announcement,
                  date,
                  errorMessage: (error as Error)?.message,
                }),
                message: ANNOUNCEMENT_BANNER_REPORT_TYPES.FAILED_TO_FORMAT_DATE,
                type: REPORT_TYPES.error,
                stack: (error as Error)?.stack,
              }),
            },
          });
        }
      }

      return undefined;
    },
    [announcement],
  );

  const formattedStartDate = useMemo(() => {
    return safelyFormatDateAndReportOnFailure(startDate);
  }, [startDate, safelyFormatDateAndReportOnFailure]);

  const formattedEndDate = useMemo(() => {
    return safelyFormatDateAndReportOnFailure(endDate);
  }, [endDate, safelyFormatDateAndReportOnFailure]);

  const announcementText = useMemo(() => {
    if (!text) {
      const context = {
        announcement,
        bannerVisible: false,
      };

      report({
        scope: EXO_SKELETON_SCOPE_NAME,
        type: REPORT_TYPES.info,
        value: ANNOUNCEMENT_BANNER_REPORT_TYPES.TEXT_EMPTY,
        context: {
          ...context,
          ...wrapWithEventPropertiesPrefix({
            data: safeStringify(context),
            message: ANNOUNCEMENT_BANNER_REPORT_TYPES.TEXT_EMPTY,
            type: REPORT_TYPES.info,
          }),
        },
      });

      return undefined;
    }

    const resolvedStartDate = formattedStartDate
      ? `from ${formattedStartDate} `
      : '';
    const resolvedEndDate = formattedEndDate
      ? `until ${formattedEndDate} `
      : '';

    return [text, resolvedStartDate, resolvedEndDate].join(' ').trim();
  }, [text, formattedStartDate, formattedEndDate, announcement]);

  const onDismiss = useCallback(() => {
    if (allowDismiss && !isDismissed) {
      setIsDismissed(true);
      saveDismissedState({ announcementId, isDismissed: true });

      const context = {
        announcement,
        bannerVisible: false,
      };

      report({
        scope: EXO_SKELETON_SCOPE_NAME,
        type: REPORT_TYPES.info,
        value: ANNOUNCEMENT_BANNER_REPORT_TYPES.USER_DISMISSED,
        context: {
          ...context,
          ...wrapWithEventPropertiesPrefix({
            data: safeStringify(context),
            message: ANNOUNCEMENT_BANNER_REPORT_TYPES.USER_DISMISSED,
            type: REPORT_TYPES.info,
          }),
        },
      });

      analytics.track(ANALYTICS_TYPES.ANNOUNCMENT_BANNER_DISMISS, {
        announcement,
      });
    }
  }, [announcement, allowDismiss, isDismissed, announcementId]);

  const hasUserAgentRegexAndDoesNotMatch = useMemo(
    () => userAgentRegex && !testUserAgentRegex(userAgentRegex),
    [userAgentRegex],
  );

  const visible = useMemo(
    () => announcementText && !hasUserAgentRegexAndDoesNotMatch && !isDismissed,
    [announcementText, hasUserAgentRegexAndDoesNotMatch, isDismissed],
  );

  useEffect(() => {
    if (visible) {
      const context = {
        announcement,
      };

      report({
        scope: EXO_SKELETON_SCOPE_NAME,
        type: REPORT_TYPES.info,
        value: ANNOUNCEMENT_BANNER_REPORT_TYPES.BANNER_VISIBLE,
        context: {
          ...context,
          ...wrapWithEventPropertiesPrefix({
            data: safeStringify(context),
            message: ANNOUNCEMENT_BANNER_REPORT_TYPES.BANNER_VISIBLE,
            type: REPORT_TYPES.info,
          }),
        },
      });

      analytics.track(ANALYTICS_TYPES.ANNOUNCMENT_BANNER_VIEW, {
        announcement,
      });
    }
  }, [visible, announcement]);

  return {
    visible,
    onDismiss,
    allowDismiss,
    announcementText,
  };
};
